import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, ShieldCheck, Sparkles, Award, Briefcase } from "lucide-react";
import { BROKER, IMAGES } from "../lib/data";

const Producer = () => {
  return (
    <section
      id="producer"
      data-testid="producer-section"
      className="relative bg-gradient-to-b from-cream/40 via-ivory to-cream/30 py-20 sm:py-28 overflow-hidden"
    >
      <div className="absolute inset-0 grid-overlay opacity-30" />
      <div className="absolute top-20 right-0 w-80 h-80 bg-gold/8 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Compact image card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className="lg:col-span-4 relative max-w-sm mx-auto lg:mx-0"
          >
            <div className="relative aspect-[4/5] overflow-hidden gradient-border">
              <img
                src={IMAGES.producer}
                alt="Zaylan Welton — Independent Life Insurance Broker"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/30 via-transparent to-transparent" />

              {/* Floating badge */}
              <div className="absolute top-4 left-4 glass-dark px-3 py-2 inline-flex items-center gap-2 text-gold-light text-[10px] tracking-[0.25em] uppercase">
                <Sparkles className="w-3 h-3" /> Licensed Producer
              </div>
            </div>

            {/* NPN stat below image */}
            <div className="absolute -bottom-5 -right-5 hidden md:flex flex-col items-end bg-ink text-ivory px-5 py-3 border border-gold">
              <span className="eyebrow text-gold text-[9px]">NPN</span>
              <span className="font-display text-lg tracking-wider">{BROKER.npn}</span>
            </div>
          </motion.div>

          {/* Text */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="lg:col-span-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-10 h-px bg-gold" />
              <span className="eyebrow text-gold">Meet Your Producer</span>
            </div>

            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink">
              {BROKER.name.split(" ")[0]}{" "}
              <span className="italic gold-text-gradient">{BROKER.name.split(" ")[1]}</span>
            </h2>
            <p className="mt-3 eyebrow text-ink/50">
              Independent Life Insurance Broker · NPN {BROKER.npn}
            </p>

            <p className="mt-6 text-ink/75 text-base sm:text-lg leading-relaxed font-light max-w-2xl">
              I built my practice on a single belief: a great broker shouldn't{" "}
              <span className="italic">sell</span> you a policy — they should help you build a plan.
              As an independent agent licensed across twelve top-rated carriers, I have the freedom to
              put your family first, always. Zero pressure, plain English, full transparency.
            </p>

            {/* Stats / values row */}
            <div className="mt-8 grid grid-cols-3 gap-4 sm:gap-6 max-w-2xl">
              {[
                { icon: Briefcase, label: "Carriers", value: "12" },
                { icon: Award, label: "Eligibility", value: "Ages 0–89" },
                { icon: ShieldCheck, label: "Coverage", value: "All 50 States" },
              ].map(({ icon: Icon, label, value }) => (
                <div key={label} className="glass p-4 sm:p-5">
                  <Icon className="w-4 h-4 text-gold mb-2" />
                  <div className="font-display text-lg sm:text-xl text-ink leading-tight">{value}</div>
                  <div className="eyebrow text-ink/50 text-[9px] mt-1">{label}</div>
                </div>
              ))}
            </div>

            {/* Contact pills */}
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={BROKER.phoneLink}
                data-testid="producer-phone-link"
                className="group inline-flex items-center gap-3 bg-ink text-ivory hover:bg-gold hover:text-ink px-5 py-3 text-sm font-medium tracking-wide transition-colors duration-300"
              >
                <Phone className="w-4 h-4" />
                {BROKER.phone}
              </a>
              <a
                href={BROKER.emailLink}
                data-testid="producer-email-link"
                className="group inline-flex items-center gap-3 border border-ink/30 hover:border-gold hover:bg-gold/10 text-ink px-5 py-3 text-sm font-medium tracking-wide transition-all duration-300"
              >
                <Mail className="w-4 h-4 text-gold" />
                <span className="break-all">{BROKER.email}</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Producer;
